interface ControlBarProps {
  micOn: boolean;
  cameraOn: boolean;
  chatOpen: boolean;
  fullscreen: boolean;
  onMicToggle: () => void;
  onCameraToggle: () => void;
  onChatToggle: () => void;
  onFullscreen: () => void;
  onNext: () => void;
  onStop: () => void;
  onReport: () => void;
  onBlock: () => void;
}

interface CtrlBtnProps {
  icon: string;
  label: string;
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
  variant?: 'default' | 'next' | 'stop';
}

const CtrlBtn = ({ icon, label, onClick, active = true, danger = false, variant = 'default' }: CtrlBtnProps) => {
  if (variant === 'next') {
    return (
      <button
        onClick={onClick}
        title={label}
        className="flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-bold text-sm hover:from-cyan-400 hover:to-violet-500 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-cyan-500/30 hover:scale-105 active:scale-95"
      >
        <i className={`${icon} text-base`} />
        <span className="hidden sm:inline">{label}</span>
      </button>
    );
  }
  if (variant === 'stop') {
    return (
      <button
        onClick={onClick}
        title={label}
        className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/35 hover:text-red-300 transition-all duration-200 cursor-pointer hover:scale-105 active:scale-95"
      >
        <i className={`${icon} text-xl`} />
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      title={label}
      className={`w-12 h-12 flex items-center justify-center rounded-full border transition-all duration-200 cursor-pointer hover:scale-105 active:scale-95 ${
        danger
          ? 'bg-red-500/15 border-red-500/30 text-red-400 hover:bg-red-500/25'
          : active
          ? 'bg-white/10 border-white/15 text-white hover:bg-white/15'
          : 'bg-white/5 border-white/10 text-white/35 hover:bg-white/8'
      }`}
    >
      <i className={`${icon} text-lg`} />
    </button>
  );
};

const ControlBar = ({
  micOn,
  cameraOn,
  chatOpen,
  fullscreen,
  onMicToggle,
  onCameraToggle,
  onChatToggle,
  onFullscreen,
  onNext,
  onStop,
  onReport,
  onBlock,
}: ControlBarProps) => {
  return (
    <div className="relative z-20 flex items-center justify-center px-4 py-4 md:py-5">
      {/* Floating glass bar */}
      <div className="flex items-center gap-2 md:gap-3 px-4 md:px-6 py-3 md:py-4 rounded-2xl bg-black/60 backdrop-blur-2xl border border-white/10 shadow-2xl">
        {/* Left controls */}
        <CtrlBtn
          icon={micOn ? 'ri-mic-line' : 'ri-mic-off-line'}
          label={micOn ? 'Mikrofonu Kapat' : 'Mikrofonu Aç'}
          onClick={onMicToggle}
          active={micOn}
        />
        <CtrlBtn
          icon={cameraOn ? 'ri-camera-line' : 'ri-camera-off-line'}
          label={cameraOn ? 'Kamerayı Kapat' : 'Kamerayı Aç'}
          onClick={onCameraToggle}
          active={cameraOn}
        />
        <CtrlBtn
          icon="ri-chat-3-line"
          label="Sohbet"
          onClick={onChatToggle}
          active={chatOpen}
        />

        {/* Divider */}
        <div className="w-px h-8 bg-white/10 mx-1" />

        {/* Center — NEXT (most prominent) */}
        <CtrlBtn
          icon="ri-skip-forward-line"
          label="Sonraki"
          onClick={onNext}
          variant="next"
        />

        {/* Divider */}
        <div className="w-px h-8 bg-white/10 mx-1" />

        {/* Right controls */}
        <CtrlBtn
          icon={fullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}
          label={fullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran'}
          onClick={onFullscreen}
          active
        />
        <CtrlBtn
          icon="ri-alarm-warning-line"
          label="Raporla"
          onClick={onReport}
          danger
        />
        <CtrlBtn
          icon="ri-user-forbid-line"
          label="Engelle"
          onClick={onBlock}
          danger
        />
        <CtrlBtn
          icon="ri-stop-circle-line"
          label="Durdur"
          onClick={onStop}
          variant="stop"
        />
      </div>
    </div>
  );
};

export default ControlBar;
